<template>
  <div class="single-destination-page-content wide">
    <div
      v-if="pageLeadImg !== ''"
      class="lead-img"
      :style="{ 'background-image': 'url(' + pageLeadImg + ')' }"
    />

    <div class="single-destination-page-main-block">
      <intro
        v-if="originValue"
        :page-title="pageTitle"
        :page-title-from-price="pageTitleFromPrice"
        :country="country"
        :intro="intro"
        :second-intro="secondIntro"
        :second-full="secondFull"
        :airport-info="airportInfo"
        :origin="originValue"
        :destin="destinValue"
        :meta-description="metaDescription"
      />

      <template v-for="(section, sectionIndex) in sections">
        <template v-if="section && section.content">
          <page-section
            v-if="section.content.type === 'table' && section.content.tableData"
            :key="sectionIndex"
            :title="section.sectionTitle"
          >
            <simple-table :table-data="section.content.tableData" />
          </page-section>

          <template v-else-if="typeof section.content.type !== 'undefined'">
            <page-section
              v-if="showSection(section)"
              :key="sectionIndex"
              :title="section.sectionTitle"
              :class="[`${section.content.type}-section`]"
            >
              <template
                v-if="section.content.type === 'gyg'"
                #content
              >
                <gyg-content :data="section.content.data" />
              </template>

              <template #default>
                <component
                  :is="`${section.content.type}Widget`"
                  :data="section.content.data"
                />
              </template>
            </page-section>
          </template>

          <page-section
            v-else
            :key="sectionIndex"
            v-inlineModal
            :title="section.sectionTitle"
            class="default"
          >
            <div
              v-dompurify-html="section.content"
              class="content"
            />
          </page-section>
        </template>
      </template>
    </div>

    <div class="wrap">
      <terms-and-conditions
        v-if="termsAndConditionsVisible"
        :title="termsAndConditions.title"
        :content="termsAndConditions.content"
      />
    </div>
  </div>
</template>

<script>
  import { mapState } from 'vuex'
  import InlineModal from 'utils/directives/InlineModal'
  import cloneDeep from 'lodash-es/cloneDeep'

  export default {
    components: {
      Intro: () =>
        import(/* webpackChunkName: "into-section" */ 'components/Destinations/Intro'),
      SimpleTable: () =>
        import(/* webpackChunkName: "simple-table" */ 'components/Tables/SimpleTable'),
      GygWidget: () =>
        import(/* webpackChunkName: "gyg-widget" */ 'components/Widgets/Gyg'),
      BlogWidget: () =>
        import(/* webpackChunkName: "blog-widget" */ 'components/Widgets/Blog'),
      DestinationsWidget: () =>
        import(
          /* webpackChunkName: "destinations-widget" */ 'components/Widgets/Destinations'
        ),
      PartnersWidget: () =>
        import(/* webpackChunkName: "partners-widget" */ 'components/Widgets/Partners'),
      BookingWidget: () =>
        import(/* webpackChunkName: "booking-widget" */ 'components/Widgets/Booking'),
      TermsAndConditions: () =>
        import(
          /* webpackChunkName: "terms-conditions" */ 'components/TermsAndConditions'
        ),
      PageSection: () =>
        import(/* webpackChunkName: "page-section" */ 'components/Destinations/Section'),
      GygContent: () =>
        import(/* webpackChunkName: "gyg-content" */ 'components/Destinations/GygContent')
    },
    directives: {
      InlineModal
    },
    computed: {
      ...mapState('singleDestination', [
        'pageLeadImg',
        'pageTitle',
        'pageTitleFromPrice',
        'blueButtonLink',
        'blueButtonText',
        'intro',
        'secondIntro',
        'secondFull',
        'airportInfo',
        'originApt',
        'sections',
        'tableData',
        'termsAndConditionsVisible',
        'termsAndConditions',
        'useEhorizonStoreData',
        'metaDescription'
      ]),
      ...mapState('eHorizonSearchForm', ['origDestData']),
      ...mapState('eHorizonSearchForm/origin', {
        originValue: 'value'
      }),
      ...mapState('eHorizonSearchForm/destin', {
        destinValue: 'value'
      }),
      ...mapState('header', ['breadcrumbs']),
      airport() {
        return this.origDestData[this.airportInfo.airport]
      },

      hasSameDestination() {
        return this.airportInfo.hasSameDestination || false
      },

      country() {
        if (!this.airport) {
          return null
        }
        return this.airport.country
      },

      countryCode() {
        if (!this.airport) {
          return null
        }
        return this.airport.countryCode
      }
    },
    watch: {
      airport() {
        this.setDestination()
        this.updateBreadCrumbs()
      }
    },
    mounted() {
      this.setDestination()
      this.updateBreadCrumbs()
      this.setInitAirport()
    },
    methods: {
      updateBreadCrumbs() {
        const length = this.breadcrumbs.length
        const breadcrumbs = cloneDeep(this.breadcrumbs)
        const prevItem = cloneDeep(this.breadcrumbs[length - 2])

        if (this.airport && prevItem && prevItem.label !== this.country) {
          prevItem.label = this.country
          prevItem.href = `${prevItem.href}?countryCode=${this.countryCode}`

          breadcrumbs.splice(length - 1, 0, prevItem)
          this.$store.commit(`header/setBreadcrumbs`, breadcrumbs)
        }
      },
      setInitAirport() {
        if (!this.airport) {
          return
        }

        this.$store.commit('eHorizonSearchForm/destin/setInitAirport', this.airport.code)
      },
      setDestination() {
        if (this.useEhorizonStoreData) {
          this.$store.commit('singleDestination/setUseEhorizonStoreData', false)
          return
        }

        this.$store.commit('eHorizonSearchForm/setSref', 'BXD')

        if (this.airport && !this.hasSameDestination) {
          this.$store.commit('eHorizonSearchForm/destin/setValue', this.airport)
        }

        if (this.origDestData[this.originApt]) {
          this.$store.commit(
            'eHorizonSearchForm/origin/setValue',
            this.origDestData[this.originApt]
          )
        }
      },
      showSection(section) {
        const widget = `${section.content.type}Widget`

        if (widget !== 'bookingWidget') {
          return true
        }

        return !(
          navigator.userAgent.indexOf('MSIE') !== -1 ||
          navigator.appVersion.indexOf('Trident/') > -1
        )
      }
    }
  }
</script>

<style
  lang="scss"
  scoped
  rel="stylesheet/scss"
>
  @import '~assets/styles/common.scss';

  .single-destination-page-content {
    font-size: 14px;

    .lead-img {
      height: 200px;
      background-repeat: no-repeat;
      background-size: cover;
      background-position: center center;

      @include bp-mf-tablet {
        height: 320px;
      }
    }

    ::v-deep .airportInformationBlock {
      order: 1;

      @include bp-mf-tablet {
        order: 0;
      }

      @include bp-mf-desktop {
        order: 1;
      }
    }

    ::v-deep p {
      font-size: em(12);
      line-height: 20px;

      @include bp-mf-tablet {
        font-size: em(16);
        line-height: 24px;
      }

      a {
        display: inline;
        color: $blue-medium;
      }
    }
  }

  .single-destination-page-main-block {
    position: relative;
    display: flex;
    flex-direction: column;

    ::v-deep img {
      max-width: 100%;
    }
  }
</style>
